import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { Box, Button, Flex, Heading, Spinner, Stack, Text } from "@chakra-ui/react";
import TopMenu from "../components/TopMenu";
import ProdEnvBanner from "../components/ProdEnvBanner";
import DevEnvBanner from "../components/DevEnvBanner";
import PageviewTracker from "../analytics/PageviewTracker";
import AuthCallback from "../auth/AuthCallback";
import { registerTokenSource } from "../api/client";
import { identifyFromAccessToken, resetAnalytics } from "../analytics/posthog";
import { clearSignedOut, isSignedOut } from "../auth/signedOut";
import Welcome from "../pages/Welcome";
import Creators from "../pages/Creators";
import CreatorDetail from "../pages/CreatorDetail";
import Works from "../pages/Works";
import ReviewQueue from "../pages/ReviewQueue";
import WorkDetail from "../pages/WorkDetail";
import Organizations from "../pages/Organizations";
import OrganizationDetail from "../pages/OrganizationDetail";
import OrganizationNew from "../pages/OrganizationNew";
import Origins from "../pages/Origins";
import OriginDetail from "../pages/OriginDetail";
import OriginNew from "../pages/OriginNew";
import EmailTemplates from "../pages/EmailTemplates";
import EmailTemplateDetail from "../pages/EmailTemplateDetail";
import Audit from "../pages/Audit";
import Settings from "../pages/Settings";
import Forbidden from "../pages/Forbidden";

function isInSupportGroup(profile: Record<string, unknown> | undefined): boolean {
  if (!profile) return false;
  const groups = profile["cognito:groups"];
  if (Array.isArray(groups)) return groups.includes("Support");
  if (typeof groups === "string") return groups.split(/[,\s]+/).includes("Support");
  return false;
}

function ProtectedShell({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const signinTriggered = useRef(false);
  // Snapshot the signed-out flag once at mount. If we re-read it on every
  // render, clicking "Sign in" would clear the flag and immediately drop into
  // the auto-signin effect, which is fine — but snapshotting also avoids
  // races with the async removeUser() call from the sign-out handler.
  const [showSignedOut, setShowSignedOut] = useState(() => isSignedOut());

  useEffect(() => {
    registerTokenSource(() => auth.user?.id_token);
    if (auth.user?.id_token) identifyFromAccessToken(auth.user.id_token);
  }, [auth.user?.id_token]);

  // Once we observe an authenticated session that the user actually wants
  // (not a stale silent-renew that briefly resurrects auth.user during a
  // post-logout landing), the signed-out flag is stale and can be cleared.
  // While `showSignedOut` is sticky we deliberately do NOT auto-clear — the
  // explicit "Sign in" button on the landing handles that.
  useEffect(() => {
    if (auth.isAuthenticated && !showSignedOut) clearSignedOut();
  }, [auth.isAuthenticated, showSignedOut]);

  // If the post-logout page somehow lands with a resurrected auth.user
  // (silent renew, race, whatever), proactively remove it so the rest of
  // ProtectedShell can render the signed-out landing instead of the app.
  useEffect(() => {
    if (showSignedOut && auth.isAuthenticated) {
      auth.removeUser().catch(() => {});
    }
  }, [showSignedOut, auth.isAuthenticated, auth]);

  useEffect(() => {
    // Use react-oidc-context's signinRedirect so it generates and persists
    // a state nonce (required to verify the callback). Skip the Cognito IdP
    // picker via extraQueryParams.
    if (
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !signinTriggered.current &&
      !showSignedOut
    ) {
      signinTriggered.current = true;
      auth.signinRedirect({
        extraQueryParams: { identity_provider: "Workspace" },
      });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth, showSignedOut]);

  if (showSignedOut) {
    return (
      <Box p={8} maxW="md" mx="auto" mt="10vh" textAlign="center">
        <Stack spacing={4}>
          <Heading size="md">You&apos;ve been signed out</Heading>
          <Text color="gray.600">Sign in again to continue.</Text>
          <Button
            colorScheme="blue"
            alignSelf="center"
            onClick={() => {
              clearSignedOut();
              setShowSignedOut(false);
              auth.signinRedirect({
                extraQueryParams: { identity_provider: "Workspace" },
              });
            }}
          >
            Sign in
          </Button>
        </Stack>
      </Box>
    );
  }

  if (auth.isLoading || !auth.isAuthenticated) {
    return (
      <Box p={8}>
        <Spinner mr={3} />
        <Text as="span">Loading…</Text>
      </Box>
    );
  }

  if (auth.error) {
    return (
      <Box p={8}>
        <Text color="red.500">Auth error: {auth.error.message}</Text>
      </Box>
    );
  }

  if (!isInSupportGroup(auth.user?.profile as Record<string, unknown> | undefined)) {
    resetAnalytics();
    return <Forbidden />;
  }

  return <>{children}</>;
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <PageviewTracker />
      <Routes>
        <Route path="/signin/callback" element={<AuthCallback />} />
        <Route
          path="/*"
          element={
            <ProtectedShell>
              <Flex direction="column" position="sticky" top={0} zIndex={10}>
                <ProdEnvBanner />
                <DevEnvBanner />
                <TopMenu />
              </Flex>
              <Routes>
                <Route index element={<Welcome />} />
                <Route path="creators" element={<Creators />} />
                <Route path="creators/:id" element={<CreatorDetail />} />
                <Route path="works" element={<Works />} />
                <Route path="works/:id" element={<WorkDetail />} />
                <Route path="review-queue" element={<ReviewQueue />} />
                <Route path="organizations" element={<Organizations />} />
                <Route path="organizations/new" element={<OrganizationNew />} />
                <Route path="organizations/:id" element={<OrganizationDetail />} />
                <Route path="origins" element={<Origins />} />
                <Route path="origins/new" element={<OriginNew />} />
                <Route path="origins/:id" element={<OriginDetail />} />
                <Route path="email-templates" element={<EmailTemplates />} />
                <Route path="email-templates/:id" element={<EmailTemplateDetail />} />
                <Route path="audit" element={<Audit />} />
                <Route path="settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ProtectedShell>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
